import { DataTablePFSEvent } from "primereact/datatable";
import React from "react";
import config from "../../../config";
import { SubLocation } from "../enums/Result";
import { Data } from "../interfaces/Data";
import { MeasurementStatusUpdate } from "../interfaces/MeasurementStatusUpdate";
import {
  Measurement,
  Result,
  ResultFileUpload,
  ResultResubmission,
  ResultValue,
  WorkOrderResultUpload,
} from "../interfaces/Result";
import { ResultComment, ResultComments } from "../interfaces/ResultComments";
import { ResultMeasurementStatusComment } from "../interfaces/ResultMeasurementStatusComment";
import { EMPTY_PAGED_RESULTS, PagedData } from "../interfaces/ResultsAPIInterfaces";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";

export type ResultListRequestType =
  | { type: "byCustomer"; customerId: number }
  | { type: "byHVSubstation"; substationId: number }
  | { type: "bySwitchgear"; switchgearId: number | string }
  | { type: "byCaptureId"; captureId: number | string; customerId: number }
  | { type: "completeByUser" }
  | { type: "partialByUser"; customerId: number };

interface Props {
  onAuthError?: () => void;
}

export default class ResultsApis extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });

  private static readonly BASE_URL: string = `${config.apiGateway.UTP2_API}/api/Results/`;

  async getPagedResultsList(
    lazyState: DataTablePFSEvent,
    requestType: ResultListRequestType,
    companyId?: number,
    siteId?: number,
  ): Promise<PagedData<Result[]>> {
    switch (requestType.type) {
      case "byCustomer": {
        if (companyId && siteId) {
          return await this.getPagedResults(
            `${ResultsApis.BASE_URL}ByCustomerAndSite/customerId/${requestType.customerId}/siteId/${siteId}`,
            lazyState,
          );
        } else if (companyId) {
          return await this.getPagedResults(
            `${ResultsApis.BASE_URL}ByCustomerAndCompany/customerId/${requestType.customerId}/companyId/${companyId}`,
            lazyState,
          );
        } else {
          return await this.getPagedResults(`${ResultsApis.BASE_URL}ByCustomer/${requestType.customerId}`, lazyState);
        }
      }
      default: {
        return Promise.reject("The request type provided was not 'byCustomer'");
      }
    }
  }

  private async getPagedResults(apiAddress: string, lazyState: DataTablePFSEvent): Promise<PagedData<Result[]>> {
    const response = await this.apiResponseHandler.post(apiAddress, JSON.stringify(lazyState), false);

    if (!response) return Promise.resolve(EMPTY_PAGED_RESULTS);

    response.result = response.result.map((result: Data<string>) => {
      return {
        ...result,
        dataValue: JSON.parse(result.dataValue) as ResultValue,
      };
    });

    return response;
  }

  getResultsList = (requestType: ResultListRequestType): Promise<Result[]> => {
    switch (requestType.type) {
      case "byHVSubstation": {
        return this.getResults(`${ResultsApis.BASE_URL}ByHVSubstation/${requestType.substationId}`);
      }
      case "bySwitchgear": {
        return this.getResults(`${ResultsApis.BASE_URL}BySwitchGear/${requestType.switchgearId}`);
      }
      case "byCaptureId": {
        return this.getResults(
          `${ResultsApis.BASE_URL}ByCaptureId/${requestType.captureId}/Customer/${requestType.customerId}`,
        );
      }
      case "completeByUser": {
        return this.getResults(`${ResultsApis.BASE_URL}completebyuser`);
      }
      case "partialByUser": {
        return this.getResults(`${ResultsApis.BASE_URL}incompletebyuser?customerId=${requestType.customerId}`);
      }
      default: {
        return Promise.reject(
          "The request type provided was not 'byHVSubstation', 'bySwitchgear', 'byCaptureId', 'completeByUser' or 'partialByUser'",
        );
      }
    }
  };

  private getResults = (apiAddress: string): Promise<Result[]> => {
    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return deserialiseDataValue(data);
    });
  };

  resubmitResult = (resultResubmission: ResultResubmission): Promise<void> => {
    var apiAddress = `${ResultsApis.BASE_URL}resubmit`;
    return this.apiResponseHandler.post(apiAddress, JSON.stringify(resultResubmission), false);
  };

  uploadResultFile = (resultFileUpload: ResultFileUpload): Promise<void> => {
    const apiAddress = ResultsApis.BASE_URL;

    let formData = new FormData();

    formData.append("ResultFile", resultFileUpload.ResultFile);

    if (resultFileUpload.CustomerId) formData.append("CustomerId", resultFileUpload.CustomerId.toString());

    if (resultFileUpload.HVSubstationId && resultFileUpload.SwitchGearId) {
      formData.append("HVSubstationId", resultFileUpload.HVSubstationId.toString());
      formData.append("SwitchGearId", resultFileUpload.SwitchGearId.toString());
    }

    return this.apiResponseHandler.postFile(apiAddress, formData);
  };

  uploadWorkOrderResultFile = (workOrderResult: WorkOrderResultUpload): Promise<void> => {
    const apiAddress = ResultsApis.BASE_URL;
    let formData = new FormData();

    formData.append("ResultFile", workOrderResult.resultFile);
    formData.append("WorkOrderId", workOrderResult.workOrderId.toString());
    formData.append("CustomerId", workOrderResult.customerId.toString());
    workOrderResult.notes && formData.append("Notes", workOrderResult.notes);
    formData.append("StartDate", workOrderResult.startDate.toISOString());
    formData.append("CompletionDate", workOrderResult.completionDate.toISOString());

    return this.apiResponseHandler.postFile(apiAddress, formData);
  };

  getResultComments = (captureId: number): Promise<ResultComments> => {
    const apiAddress = `${ResultsApis.BASE_URL}Capture/${captureId}/Comments`;

    return this.apiResponseHandler.get(apiAddress);
  };

  createResultComments = async (captureId: number, commentText: string): Promise<number> => {
    const apiAddress = `${ResultsApis.BASE_URL}Capture/${captureId}/Comments`;

    return this.apiResponseHandler.post(apiAddress, JSON.stringify(commentText), false);
  };

  updateResultComments = async (captureId: number, comment: Partial<ResultComment>): Promise<number> => {
    const apiAddress = `${ResultsApis.BASE_URL}Capture/${captureId}/Comments`;

    return this.apiResponseHandler.put(apiAddress, JSON.stringify(comment), false);
  };

  public updateMeasurementStatus = async (update: MeasurementStatusUpdate): Promise<number> => {
    const apiAddress = `${ResultsApis.BASE_URL}Capture/${update.captureId}/Customer/${update.customerId}/Measurement/RAGStatus`;

    if (update.subLocation === null) update.subLocation = SubLocation.None;

    const body = {
      ...update,
      subLocation: update.subLocation || undefined,
    };

    return this.apiResponseHandler.put(apiAddress, JSON.stringify(body));
  };

  getResultMeasurementStatusComments = async (
    captureId: number,
    customerId: number,
    panel360AssetId: number,
    panelNo: string,
    measurement: Measurement,
  ): Promise<ResultMeasurementStatusComment[]> => {
    const apiAddress = `${ResultsApis.BASE_URL}Capture/${captureId}/Customer/${customerId}/Measurement/RAGStatus/Comments/Search`;

    if (measurement.SubLocation === null) measurement.SubLocation = SubLocation.None;

    const body = {
      Panel360AssetId: panel360AssetId,
      PanelNo: panelNo,
      MeasurementType: measurement.MeasurementType,
      MeasurementComponent: measurement.ComponentType,
      SubLocation: measurement.SubLocation,
    };

    return this.apiResponseHandler.post(apiAddress, JSON.stringify(body));
  };

  getHistoricalPlotPosition = async (
    customerId: number,
    captureId: number,
    dataPointId: number,
    measurementType: string,
    componentAssetId: number,
    subLocation: string,
  ): Promise<number> => {
    // We need to remove the dollar sign which is part of the enum attribute property otherwise the API endpoint will not recognise them as valid enums.
    measurementType =
      measurementType.charAt(0) === "$" ? measurementType.substring(1, measurementType.length) : measurementType;
    subLocation = subLocation.charAt(0) === "$" ? subLocation.substring(1, subLocation.length) : subLocation;

    const apiAddress = `${ResultsApis.BASE_URL}HistoricalPlotPosition?customerId=${customerId}&captureId=${captureId}&dataPointId=${dataPointId}&measurementType=${measurementType}&componentAssetId=${componentAssetId}&subLocation=${subLocation}`;

    return await this.apiResponseHandler.get(apiAddress);
  };
}

function deserialiseDataValue(results: Data<string>[]): Result[] {
  return results.map((result: Data<string>) => {
    return {
      ...result,
      dataValue: JSON.parse(result.dataValue) as ResultValue,
    };
  });
}
